import { useState } from 'react';
import { useTestMode } from '../lib/testModeContext.jsx';
import { useTrip } from '../lib/tripContext.jsx';
import { useUser } from '../lib/userContext.jsx';
import AdversarialPanel from './AdversarialPanel.jsx';

// The persistent test-mode control panel (spec §15). Floats below the
// banner when expanded. Mobile-friendly width; not a modal so the user
// can interact with the app behind it for verification.

export default function TestModePanel({ onClose }) {
  const mode = useTestMode();
  const { trip } = useTrip();
  const { activePlayerId, setActivePlayer } = useUser();
  const [manual, setManual] = useState({ lat: '', lng: '' });
  const [showAdversarial, setShowAdversarial] = useState(false);

  const presets = trip?.test_mode_locations || [];
  const players = trip?.players || [];

  const setManualLocation = () => {
    const lat = parseFloat(manual.lat);
    const lng = parseFloat(manual.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    mode.setMockLocation({ lat, lng, label: `manual ${lat.toFixed(3)}, ${lng.toFixed(3)}` });
  };

  const onPickPreset = (preset) => {
    mode.setMockLocation({ lat: preset.lat, lng: preset.lng, label: preset.label });
  };

  const onSetTime = (e) => {
    const v = e.target.value;
    if (!v) {
      mode.setMockTimeIso(null);
      return;
    }
    // <input type="datetime-local"> emits "YYYY-MM-DDTHH:mm" in local time.
    // Parse naive then build an ISO string.
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return;
    mode.setMockTimeIso(d.toISOString());
  };

  return (
    <aside
      role="region"
      aria-label="Test mode panel"
      className="sticky top-[2.25rem] z-30 mx-auto max-w-screen-sm border-b border-yellow-500/40 bg-bg-primary/95 backdrop-blur"
    >
      <div className="space-y-3 p-3">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="font-mono text-[10px] uppercase tracking-wider text-yellow-700 dark:text-yellow-200">
            test mode panel
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary underline-offset-4 hover:underline"
          >
            close
          </button>
        </div>

        <Section label="mock GPS">
          <div className="flex flex-wrap gap-1.5">
            {presets.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => onPickPreset(p)}
                className={[
                  'rounded-full border px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition',
                  mode.mockLocation?.label === p.label
                    ? 'border-action bg-action text-action-fg'
                    : 'border-border-tertiary bg-bg-secondary text-fg-secondary hover:bg-bg-tertiary',
                ].join(' ')}
              >
                {p.label}
              </button>
            ))}
            {mode.mockLocation && (
              <button
                type="button"
                onClick={() => mode.setMockLocation(null)}
                className="rounded-full border border-border-tertiary bg-bg-secondary px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary hover:bg-bg-tertiary"
              >
                use real GPS
              </button>
            )}
          </div>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              inputMode="decimal"
              placeholder="lat"
              value={manual.lat}
              onChange={(e) => setManual((m) => ({ ...m, lat: e.target.value }))}
              className="w-24 rounded-md border border-border-tertiary bg-bg-primary px-2 py-1 font-mono text-xs"
            />
            <input
              type="text"
              inputMode="decimal"
              placeholder="lng"
              value={manual.lng}
              onChange={(e) => setManual((m) => ({ ...m, lng: e.target.value }))}
              className="w-24 rounded-md border border-border-tertiary bg-bg-primary px-2 py-1 font-mono text-xs"
            />
            <button
              type="button"
              onClick={setManualLocation}
              className="rounded-md border border-action bg-action px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-action-fg"
            >
              set
            </button>
          </div>
        </Section>

        <Section label="time travel">
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="datetime-local"
              value={mode.mockTimeIso ? toLocalDatetimeInput(mode.mockTimeIso) : ''}
              onChange={onSetTime}
              className="rounded-md border border-border-tertiary bg-bg-primary px-2 py-1 font-mono text-xs"
            />
            {mode.mockTimeIso && (
              <button
                type="button"
                onClick={() => mode.setMockTimeIso(null)}
                className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary underline-offset-4 hover:underline"
              >
                clear
              </button>
            )}
          </div>
          <p className="mt-1 font-mono text-[9px] uppercase tracking-wider text-fg-tertiary">
            engine &ldquo;now&rdquo; = {(mode.now()).toLocaleString()}
          </p>
        </Section>

        <Section label="player switcher">
          <div className="flex flex-wrap gap-1.5">
            {players.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setActivePlayer(p.id)}
                className={[
                  'rounded-full border px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition',
                  activePlayerId === p.id
                    ? 'border-action bg-action text-action-fg'
                    : 'border-border-tertiary bg-bg-secondary text-fg-secondary hover:bg-bg-tertiary',
                ].join(' ')}
              >
                {p.display_name || p.name}
              </button>
            ))}
          </div>
          <p className="mt-1 font-mono text-[9px] uppercase tracking-wider text-fg-tertiary">
            client-side player switch only — server endpoints still derive player_id from the session cookie.
          </p>
        </Section>

        <Section label="spectator preview">
          <a
            href="/spectator"
            target="_blank"
            rel="noreferrer noopener"
            className="inline-block rounded-md border border-action bg-action px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-action-fg"
          >
            open Anar&apos;s view ↗
          </a>
        </Section>

        <Section label="adversarial · red team">
          <button
            type="button"
            onClick={() => setShowAdversarial((v) => !v)}
            className="rounded-md border border-border-tertiary bg-bg-secondary px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-fg-secondary hover:bg-bg-tertiary"
          >
            {showAdversarial ? 'hide' : 'open'} adversarial panel
          </button>
          {showAdversarial && <AdversarialPanel />}
        </Section>

        <Section label="reset">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => mode.reset()}
              className="rounded-md border border-danger/60 bg-danger/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-danger"
            >
              exit test mode
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm('Reset test-mode state? This wipes mock GPS + clock and clears the local IndexedDB queue.')) return;
                mode.setMockLocation(null);
                mode.setMockTimeIso(null);
                try {
                  const { default: idbDelete } = await Promise.resolve({
                    default: () => new Promise((res, rej) => {
                      const req = indexedDB.deleteDatabase('somesay-v1');
                      req.onsuccess = () => res();
                      req.onerror = () => rej(req.error);
                    }),
                  });
                  await idbDelete();
                } catch {
                  /* ignore */
                }
              }}
              className="rounded-md border border-border-tertiary bg-bg-secondary px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-fg-secondary"
            >
              reset state
            </button>
          </div>
        </Section>
      </div>
    </aside>
  );
}

function Section({ label, children }) {
  return (
    <section>
      <p className="font-mono text-[9px] uppercase tracking-wider text-fg-tertiary">{label}</p>
      <div className="mt-1">{children}</div>
    </section>
  );
}

function toLocalDatetimeInput(iso) {
  // Format for <input type="datetime-local">: YYYY-MM-DDTHH:mm in local time.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
